import gql from 'graphql-tag';

import {
  COUPON_FRAGMENT,
  DISCOUNT_ACTIVITY_FRAGMENT,
  FULL_DISCOUNT_PRESENT_FRAGMENT,
  PACKAGE_DISCOUNT_FRAGMENT,
  PURCHASE_PREMIUM_FRAGMENT,
  SELECTIVE_GIFT_ACTIVITY_FRAGMENT,
  SHOPPING_CREDITS_CLAIM_ACTIVITY_FRAGMENT,
  SHOPPING_CREDITS_DEDUCTION_ACTIVITY_FRAGMENT,
} from '../fragments';

/**
 * 场景三：商品详情页合并查询 - 商品数据组
 * 合并内容：product + productActivitiesFindAll + activityCountdownByProduct + getBuyerShowDetail
 * 纯商品维度数据，仅依赖 productId，不查用户信息
 */
export const PRODUCT_DETAIL_INIT_QUERY = gql`
  query ProductDetailInit($id: ID!, $productId: ID!) {
    # 商品详情（沿用 fetchProductDetailDocument 字段）
    product(id: $id) {
      id
      name
      description
      enabled
      productTotalStock
      pointsExchangeInfo {
        id
        exchangeLimit
        allowPurchaseAtOriginalPrice
        exchangeConditionType
        pointExchange {
          points
          cash
          exchangeTotal
        }
      }
      productPurchasePermission {
        id
        productId
        isMembershipPlanPurchase
        membershipPlans {
          id
        }
        guideMembershipPlanId
      }
      memberPriceActivityAmount {
        minDiscount
        maxDiscount
        minMemberPriceAmount
        maxMemberPriceAmount
        memberPriceProductVariant {
          memberPriceAmount
          productVariantId
          memberDiscount
          discountType
        }
      }
      optionGroups {
        id
        code
        name
        customFields {
          priority
        }
        options {
          id
          code
          name
          customFields {
            priority
          }
        }
      }
      variants {
        id
        name
        exclusionGroupsTypeName
        isCanPurchase
        pointExchange {
          points
          cash
          exchangeTotal
        }
        customFields {
          virtualTargetType
        }
        virtualTarget {
          ... on Coupon {
            id
            cname: name
            cstate: state
            enable
            createdAt
            updatedAt
            remarks
            type
            preferentialContent {
              preferentialType
              minimum
              discount
              maximumOffer
              includingDiscountProducts
            }
            validityPeriod {
              type
              startTime
              endTime
              numberOfDays
            }
            totalQuantity
            applicableProduct {
              applicableType
              productIds
            }
            whetherRestrictUsers
            memberPlanIds
            introduce
            promotion {
              id
            }
            activityContent
            activityTime
          }
          ... on MembershipPlan {
            id
            name
            state
          }
        }
        options {
          code
          name
          id
          group {
            id
            code
            name
          }
        }
        featuredAsset {
          source
        }
        isThereAnyStock
        price
        sku
      }
      featuredAsset {
        source
      }
      assets {
        id
        width
        height
        name
        preview
        source
        type
      }
      customFields {
        price
        markingPrice
        isVipProduct
        unit
        particulars
        limitType
        limitCount
        productType
        virtualTargetType
        isHiddenCart
        subscriptionPlan {
          id
          periodAndDiscount {
            expireNumberOfPeriod
            discountType
            discountAmount
            recommend
          }
          subscriptionInterval {
            unit
            frequency
          }
        }
        putOnSaleType
        putOnSaleTime
        shareCover
      }
      participatingActivities {
        id
        createdAt
        updatedAt
        endsAt
        couponCode
        perCustomerUsageLimit
        enabled
        customFields {
          activityName
          type
        }
      }
      offlineSales {
        offlineSales
      }
    }

    # 商品参与的所有活动（沿用 fetchProductActivitiesFindAllDocument 字段和 fragments）
    productActivitiesFindAll(productId: $productId) {
      ... on Coupon {
        ...Coupon
      }
      ... on PurchasePremium {
        ...PurchasePremium
      }
      ... on DiscountActivity {
        ...DiscountActivity
      }
      ... on FullDiscountPresent {
        ...FullDiscountPresent
      }
      ... on PackageDiscount {
        ...PackageDiscount
      }
      ... on SelectiveGiftActivity {
        ...SelectiveGiftActivity
      }
      ... on ShoppingCreditsClaimActivity {
        ...ShoppingCreditsClaimActivity
      }
      ... on ShoppingCreditsDeductionActivity {
        ...ShoppingCreditsDeductionActivity
      }
    }

    # 活动倒计时（沿用 fetchActivityCountdownByProductDocument 字段）
    activityCountdownByProduct(productId: $productId) {
      id
      createdAt
      updatedAt
      activityType
      activityId
      name
      countdownTime
      priority
    }

    # 买家秀（沿用 fetchGetBuyerShowDetailDocument 字段）
    getBuyerShowDetail(productId: $productId) {
      enabled
      categoryIds
      pictures
      totalItems
    }
  }
  ${COUPON_FRAGMENT}
  ${PURCHASE_PREMIUM_FRAGMENT}
  ${DISCOUNT_ACTIVITY_FRAGMENT}
  ${FULL_DISCOUNT_PRESENT_FRAGMENT}
  ${PACKAGE_DISCOUNT_FRAGMENT}
  ${SELECTIVE_GIFT_ACTIVITY_FRAGMENT}
  ${SHOPPING_CREDITS_CLAIM_ACTIVITY_FRAGMENT}
  ${SHOPPING_CREDITS_DEDUCTION_ACTIVITY_FRAGMENT}
`;

/**
 * 场景三：商品详情页合并查询 - 用户限购数据组
 * 合并内容：getExchangeableQuantity + getPurchaseQuantity(regularOrder) + getPurchaseQuantity(shoppingTrolley)
 * 需要用户身份（登录态），查询当前用户对该商品的购买/兑换数量限制
 */
export const USER_PURCHASE_LIMIT_QUERY = gql`
  query UserPurchaseLimit(
    $productId: ID!
    $exchangeType: OrderPurchaseType
    $isIncludeCurrentOrder: Boolean
  ) {
    # 积分兑换可兑换数量（沿用 fetchGetExchangeableQuantityDocument 参数）
    getExchangeableQuantity(
      productId: $productId
      type: $exchangeType
      isIncludeCurrentOrder: $isIncludeCurrentOrder
    )

    # 常规订单已购数量
    regularOrderQuantity: getPurchaseQuantity(
      productId: $productId
      type: regularOrder
      isIncludeCurrentOrder: false
    )

    # 购物车中已加入数量
    trolleyQuantity: getPurchaseQuantity(
      productId: $productId
      type: shoppingTrolley
      isIncludeCurrentOrder: true
    )
  }
`;

